import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import i18n from 'culture/i18n';
import getStartedSteps from './getStartedSteps';
import { markTaskCompleted, getCompletedTasks } from './useOnboardingProgress';

export const TASKS_CONFIG = getStartedSteps;

const t = (key, vars) => i18n.t(key, vars);

const SESSION_KEY = 'onboarding_active_task';

const saveActiveState = (taskKey, phase) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ taskKey, phase }));
    window.dispatchEvent(new Event('onboarding:activeStateChanged'));
  } catch (err) {
    console.warn('[Coachmark] Could not save session state:', err);
  }
};

const clearActiveState = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(new Event('onboarding:activeStateChanged'));
  } catch (err) {
    console.warn('[Coachmark] Could not clear session state:', err);
  }
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForElement = (selector, timeoutMs = 15000) =>
  new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) return resolve(existing);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);
  });

const waitForElements = (selector, timeoutMs = 15000) =>
  new Promise((resolve) => {
    const existing = document.querySelectorAll(selector);
    if (existing.length > 0) return resolve(Array.from(existing));

    const observer = new MutationObserver(() => {
      const els = document.querySelectorAll(selector);
      if (els.length > 0) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(Array.from(els));
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      observer.disconnect();
      resolve([]);
    }, timeoutMs);
  });

const COACHMARK_WIDTH = 270;
const COACHMARK_MIN_HEIGHT = 140;
const VIEWPORT_MARGIN = 20;
const ARROW_OFFSET = 6;
const HEADER_HEIGHT = 65;

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
const getViewport = () => ({
  vw: document.documentElement.clientWidth || window.innerWidth,
  vh: window.innerHeight
});

const placeForDrawer = (r, w, h) => {
  const GAP = 14;
  const { vh } = getViewport();
  const hasSpaceLeft = r.left >= w + GAP + VIEWPORT_MARGIN;
  const maxTop = Math.max(70, vh - h - VIEWPORT_MARGIN);

  if (hasSpaceLeft) {
    const left = r.left - w - GAP;
    const top = clamp(r.top + 80, 70, maxTop);
    const arrowTop = clamp(r.top + 100 - top, VIEWPORT_MARGIN, Math.min(100, h - 28));
    return { placement: 'left', left, top, arrowTop, arrowLeft: null };
  }

  const left = Math.max(VIEWPORT_MARGIN, r.right - w - 20);
  const top = clamp(r.top + 70, 70, maxTop);
  return { placement: 'bottom', left, top, arrowLeft: 120, arrowTop: null };
};

const placeForSidebar = (el, r, w, h) => {
  const { vw, vh } = getViewport();
  const nav = el.closest('.navbar-vertical');
  const edge = nav ? nav.getBoundingClientRect().right : r.right;
  const centerY = r.top + r.height / 2;

  const left = Math.min(Math.max(edge, r.right) + 12, vw - w - VIEWPORT_MARGIN);
  const top = clamp(centerY - 40, VIEWPORT_MARGIN, vh - h - VIEWPORT_MARGIN);
  const arrowTop = clamp(centerY - top - ARROW_OFFSET, VIEWPORT_MARGIN, Math.min(90, h - 28));
  return { placement: 'right', left, top, arrowTop, arrowLeft: null };
};

const placeForButton = (r, w, h) => {
  const { vw, vh } = getViewport();
  const centerX = r.left + r.width / 2;
  const left = clamp(centerX - w / 2, VIEWPORT_MARGIN, vw - w - VIEWPORT_MARGIN);
  const arrowLeft = clamp(centerX - left - ARROW_OFFSET, 16, w - 28);

  if (r.bottom + 12 + h <= vh - 10) {
    return { placement: 'bottom', left, top: r.bottom + 12, arrowLeft, arrowTop: null };
  }
  return {
    placement: 'top',
    left,
    top: Math.max(HEADER_HEIGHT, r.top - h - 12),
    arrowLeft,
    arrowTop: null
  };
};

const placeForAccordion = (el, w, h) => {
  const { vh } = getViewport();
  const containerRect = el.getBoundingClientRect();

  const GAP = 2; 
  if (containerRect.left < w + GAP + ARROW_OFFSET + VIEWPORT_MARGIN) {
    return placeForButton(containerRect, w, h);
  }

  const left = Math.max(VIEWPORT_MARGIN, containerRect.left - w - (GAP + ARROW_OFFSET));
  const top = clamp(containerRect.top + 10, 70, Math.max(70, vh - h - VIEWPORT_MARGIN));
  const arrowTop = clamp(containerRect.top + 35 - top, VIEWPORT_MARGIN, Math.min(100, h - 28));

  return { placement: 'left', left, top, arrowLeft: null, arrowTop };
};

const detectType = (el) => {
  if (el.classList.contains('new-planning-offcanvas') || el.classList.contains('offcanvas')) return 'drawer';
  if (el.closest('.navbar-vertical, .navbar-vertical-content, .sidebar')) return 'sidebar';
  if (
    el.classList.contains('rf-accordion-item') ||
    el.classList.contains('accordion-item') ||
    el.classList.contains('accordion-header') ||
    el.classList.contains('card') ||
    el.dataset?.tour === 'tour-signature-card'
  ) {
    return 'accordion';
  }
  return 'button';
};

const calculatePlacement = (el, w, h) => {
  const type = detectType(el);
  if (type === 'accordion') return placeForAccordion(el, w, h);
  const r = el.getBoundingClientRect();
  if (type === 'drawer') return placeForDrawer(r, w, h);
  if (type === 'sidebar') return placeForSidebar(el, r, w, h);
  return placeForButton(r, w, h);
};

let showCoachmarkInternal = null;
let hideCoachmarkInternal = null;

export const showCoachmark = (el, title, text, btnLabel, onBtn, onBack = null, backLabel = null) => {
  const handler = () => {
    if (onBtn) onBtn();
    hideCoachmark();
  };
  const backHandler = onBack
    ? () => {
        onBack();
      }
    : null;
  if (showCoachmarkInternal) {
    showCoachmarkInternal(el, title, text, btnLabel, handler, backHandler, backLabel);
  }
};

export const hideCoachmark = () => {
  if (hideCoachmarkInternal) hideCoachmarkInternal();
};

const showCoachmarkAsync = (el, title, text, btnLabel, backLabel = null) =>
  new Promise((resolve) => {
    let resolved = false;
    const onNext = () => {
      if (resolved) return;
      resolved = true;
      hideCoachmark();
      resolve('next');
    };
    const onBack = backLabel
      ? () => {
          if (resolved) return;
          resolved = true;
          hideCoachmark();
          resolve('back');
        }
      : null;

    showCoachmark(el, title, text, btnLabel, onNext, onBack, backLabel);
  });

const completeTask = (task) => {
  clearActiveState();
  markTaskCompleted(task.id);

  const totalTasks = Object.keys(getStartedSteps).length;
  const currentCompleted = getCompletedTasks().length;

  if (currentCompleted < totalTasks) {
    setTimeout(() => {
      window.dispatchEvent(new Event('onboarding:openGetStarted'));
    }, 500);
  }
};

const readTitleFromHeader = (item) => {
  const header = item.querySelector('.accordion-header, .card-header');
  if (!header) return null;
  const clone = header.cloneNode(true);
  clone.querySelectorAll('.rf-header-subtitle, .rf-header-icon, svg, i').forEach((el) => el.remove());
  return clone.textContent.trim() || null;
};

const getSectionTitle = (item, task, index) => {
  const titleSpan =
    item.querySelector('.rf-header-text > span:not(.rf-header-subtitle)') ||
    item.querySelector('.rf-header-text > span:first-child');
  const txt = titleSpan?.textContent.trim() || readTitleFromHeader(item);
  return txt || `${t(task.titleKey)} - ${t('cm_stepFallback', { step: index + 1 })}`;
};

const expandParentMenu = async (navEl) => {
  const collapse = navEl.closest('.collapse');
  if (!collapse || collapse.classList.contains('show')) return true;

  const toggleBtn = collapse.closest('li')?.querySelector('.dropdown-indicator');
  if (!toggleBtn) return true;

  toggleBtn.click();

  for (let i = 0; i < 8; i++) {
    await delay(100);
    if (collapse.classList.contains('show')) return true;
  }
  await delay(150);
  return true;
};

const isPageSaveButton = (btn) =>
  !btn.classList.contains('btn-close') &&
  !btn.closest('.navbar, .navbar-top, .sidebar, .modal');

const showNavPhase = async (task, navigate) => {
  const navEl = await waitForElement(task.navSelector, 4000);
  if (!navEl) {
    navigate(task.listPath);
    await delay(350);
    return 'next';
  }

  await expandParentMenu(navEl);
  await delay(250);

  navEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  let userClickedNav = false;

  const result = await new Promise((resolve) => {
    let resolved = false;

    const onNavClick = () => {
      if (resolved) return;
      resolved = true;
      userClickedNav = true;
      cleanup();
      resolve('next');
    };

    const onNext = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve('next');
    };

    const onBack = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve('back');
    };

    const cleanup = () => {
      navEl.removeEventListener('click', onNavClick);
      hideCoachmark();
    };

    navEl.addEventListener('click', onNavClick);

    showCoachmark(
      navEl,
      t(task.labelKey),
      t('cm_clickToOpen', { title: t(task.titleKey) }),
      t('cm_gotIt'),
      onNext,
      onBack,
      t('onboardingTour.back', 'Back')
    );
  });

  if (result === 'next' && !userClickedNav) {
    navigate(task.listPath);
    await delay(450);
  }

  return result;
};

const showNewButtonPhase = async (task, navigate) => {
  const btnEl = await waitForElement(task.btnSelector, 12000);
  if (!btnEl) {
    console.warn('[Coachmark] Button not found:', task.btnSelector);
    return 'next';
  }
  btnEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  let userClickedTarget = false;

  const result = await new Promise((resolve) => {
    let resolved = false;

    const onTargetClick = () => {
      if (resolved) return;
      resolved = true;
      userClickedTarget = true;
      cleanup();
      resolve('next');
    };

    const onNext = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve('next');
    };

    const onBack = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve('back');
    };

    const cleanup = () => {
      btnEl.removeEventListener('click', onTargetClick);
      hideCoachmark();
    };

    btnEl.addEventListener('click', onTargetClick);

    showCoachmark(
      btnEl,
      t('cm_createNew', { singular: t(task.singularKey) }),
      t('cm_clickToAdd', { singular: t(task.singularKey) }),
      t('cm_gotIt'),
      onNext,
      onBack,
      t('onboardingTour.back', 'Back')
    );
  });

  if (result === 'next' && !userClickedTarget) {
    if (task.formPath) {
      navigate(task.formPath);
    } else {
      btnEl.click();
    }
    await delay(450);
  }

  return result;
};

const showWizardSections = async (task, items, startIndex = 0) => {
  let i = Math.min(startIndex, items.length - 1);
  if (i < 0) i = 0;

  while (i >= 0 && i < items.length) {
    const item = items[i];

    const headerBtn = item.querySelector('.accordion-button');
    if (headerBtn && headerBtn.classList.contains('collapsed')) {
      headerBtn.click();
      await delay(150);
    }

    item.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const sectionTitle = getSectionTitle(item, task, i);
    const isLast = i === items.length - 1;
    const btnLabel = t('cm_nextStep', 'Next');
    const backLabel = t('onboardingTour.back', 'Back');

    await delay(300);
    const action = await showCoachmarkAsync(item, sectionTitle, t('cm_fillSection'), btnLabel, backLabel);

    if (action === 'back') {
      if (i === 0) {
        return { action: 'back', target: 'button' };
      }
      i--;
      await delay(200);
    } else if (action === 'next') {
      if (isLast) {
        return { action: 'next' };
      }
      i++;
      await delay(250);
    } else {
      return { action: 'dismiss' };
    }
  }

  return { action: 'next' };
};

const showDrawerSection = async (task) => {
  const drawer = await waitForElement(task.formSelector, 3750);
  if (!drawer) return { action: 'next' };
  await delay(400);

  const action = await showCoachmarkAsync(
    drawer,
    t('cm_planningDetails'),
    t('cm_planningFill'),
    t('cm_nextStep', 'Next'),
    t('onboardingTour.back', 'Back')
  );

  if (action === 'back') {
    return { action: 'back', target: 'button' };
  }
  return { action: 'next' };
};

const showFormPhase = async (task, navigate, startIndex = 0) => {
  if (task.mode === 'drawer') {
    return await showDrawerSection(task);
  }

  const items = await waitForElements(task.formSelector, 15000);
  if (items.length > 0) {
    return await showWizardSections(task, items, startIndex);
  }

  const container = document.querySelector('.rf-right-panel, main .card');
  if (container) {
    const action = await showCoachmarkAsync(
      container,
      t('cm_fillTitle', { title: t(task.titleKey) }),
      t('cm_fillDetails'),
      t('cm_nextStep', 'Next'),
      t('onboardingTour.back', 'Back')
    );
    if (action === 'back') {
      return { action: 'back', target: 'button' };
    }
  }
  return { action: 'next' };
};

const showSavePhase = async (task) => {
  const firstSaveBtn = await waitForElement(task.saveSelector, 6000);
  if (!firstSaveBtn) return 'cancelled';

  const allBtns = Array.from(document.querySelectorAll(task.saveSelector));
  const saveBtn = allBtns.find(isPageSaveButton);
  if (!saveBtn) return 'cancelled';

  saveBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const title = task.mode === 'drawer'
    ? t('cm_savePlanning')
    : t('cm_save', { singular: t(task.singularKey) });
  const text = task.mode === 'drawer'
    ? t('cm_clickSavePlanning')
    : t('cm_clickSave', { singular: t(task.singularKey) });

  return await new Promise((resolve) => {
    let resolved = false;

    const onSaveClick = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve('next');
    };

    const onNext = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve('next');
    };

    const onBack = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve('back');
    };

    const cleanup = () => {
      saveBtn.removeEventListener('click', onSaveClick);
      hideCoachmark();
    };

    saveBtn.addEventListener('click', onSaveClick);

    showCoachmark(
      saveBtn,
      title,
      text,
      t('cm_gotIt'),
      onNext,
      onBack,
      t('onboardingTour.back', 'Back')
    );
  });
};

const showCardPhase = async (task) => {
  const cardEl = await waitForElement(task.cardSelector, 12000);
  if (!cardEl) {
    console.warn('[Coachmark] Card not found:', task.cardSelector);
    return 'next';
  }
  cardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  await delay(300);

  const title = t('cm_signatureTitle', t('userSignature', 'User Signature'));
  const text = t('cm_signatureDesc', 'Set up your signature to be applied to all proposals shared with clients.');
  const btnLabel = t('cm_gotIt', 'Got it');
  const backLabel = t('onboardingTour.back', 'Back');

  return await showCoachmarkAsync(cardEl, title, text, btnLabel, backLabel);
};

const runTaskGuide = async (task, navigate, startPhase = 'nav') => {
  let phase = startPhase;
  let accordionIndex = 0;

  while (phase && phase !== 'done') {
    if (phase === 'nav') {
      saveActiveState(task.id, 'nav');
      const action = await showNavPhase(task, navigate);
      if (action === 'back') {
        clearActiveState();
        hideCoachmark();
        window.dispatchEvent(new Event('onboarding:openGetStarted'));
        return;
      } else if (action === 'next') {
        phase = task.mode === 'card' ? 'card' : 'button';
      } else {
        return;
      }
    } else if (phase === 'card') {
      saveActiveState(task.id, 'card');
      const action = await showCardPhase(task);
      if (action === 'back') {
        phase = 'nav';
      } else if (action === 'next') {
        completeTask(task);
        phase = 'done';
      } else {
        return;
      }
    } else if (phase === 'button') {
      saveActiveState(task.id, 'button');
      const action = await showNewButtonPhase(task, navigate);
      if (action === 'back') {
        phase = 'nav';
      } else if (action === 'next') {
        phase = 'form';
        accordionIndex = 0;
      } else {
        return;
      }
    } else if (phase === 'form') {
      saveActiveState(task.id, 'form');
      const result = await showFormPhase(task, navigate, accordionIndex);
      if (result.action === 'back') {
        if (result.target === 'button') {
          if (task.listPath) {
            navigate(task.listPath);
            await delay(400);
          }
          phase = 'button';
        } else if (typeof result.targetIndex === 'number') {
          accordionIndex = result.targetIndex;
          phase = 'form';
        }
      } else if (result.action === 'next') {
        phase = 'save';
      } else {
        return;
      }
    } else if (phase === 'save') {
      const action = await showSavePhase(task);
      if (action === 'back') {
        phase = 'form';
        accordionIndex = 999;
      } else if (action === 'next') {
        completeTask(task);
        phase = 'done';
      } else {
        return;
      }
    }
  }
};

export const startTaskGuide = (taskKey, navigate) => {
  const task = getStartedSteps[taskKey];
  if (!task) return;
  hideCoachmark();
  runTaskGuide(task, navigate, 'nav');
};

const readRect = (el) => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const isSameRect = (a, b) =>
  Math.abs(a.top - b.top) < 0.5 &&
  Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 &&
  Math.abs(a.height - b.height) < 0.5;

const Coachmark = () => {
  const { t: tHook } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [placement, setPlacement] = useState('bottom');
  const ringRef = useRef(null);
  const popoverRef = useRef(null);
  const isResumingRef = useRef(false);
  const isOpen = Boolean(data);

  const clearRing = () => {
    if (ringRef.current) {
      if (ringRef.current.dataset.tempActive === 'true') {
        ringRef.current.classList.remove('active');
        delete ringRef.current.dataset.tempActive;
      }
      ringRef.current.classList.remove('coachmark-target');
      ringRef.current = null;
    }
  };

  const handleDismiss = () => {
    clearActiveState();
    clearRing();
    setData(null);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && data) {
        handleDismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [data]);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved && !isResumingRef.current) {
        const { taskKey, phase } = JSON.parse(saved);
        const task = getStartedSteps[taskKey];
        if (task) {
          if (task.formPath && location.pathname === task.formPath && phase === 'form') {
            isResumingRef.current = true;
            runTaskGuide(task, navigate, 'form').finally(() => {
              isResumingRef.current = false;
            });
          } else if (location.pathname === task.listPath && phase === 'button') {
            isResumingRef.current = true;
            runTaskGuide(task, navigate, 'button').finally(() => {
              isResumingRef.current = false;
            });
          } else if (location.pathname === task.listPath && phase === 'card') {
            isResumingRef.current = true;
            runTaskGuide(task, navigate, 'card').finally(() => {
              isResumingRef.current = false;
            });
          }
        }
      }
    } catch (err) {
      console.warn('[Coachmark] Error restoring session:', err);
    }
  }, [location.pathname]);

  useEffect(() => {
    showCoachmarkInternal = (el, title, text, btnLabel, onBtn, onBack = null, backLabel = null) => {
      if (!el) return;
      clearRing();
      const isNav = Boolean(el.closest('.navbar-vertical, .sidebar') || el.classList.contains('nav-link'));
      const isHat = Boolean(
        el.getAttribute?.('data-tour') === 'tour-retake' ||
        el.closest?.('[data-tour="tour-retake"]')
      );
      if (isNav) {
        if (!el.classList.contains('active')) {
          el.classList.add('active');
          el.dataset.tempActive = 'true';
        }
      }
      el.classList.add('coachmark-target');
      ringRef.current = el;

      // Position is calculated in useLayoutEffect using the real popover size
      setData({
        title,
        text,
        btnLabel,
        onBtn,
        onBack,
        backLabel,
        isNav,
        isHat,
        targetRect: readRect(el)
      });
    };

    hideCoachmarkInternal = () => {
      clearRing();
      setData(null);
    };

    return () => {
      clearRing();
      showCoachmarkInternal = null;
      hideCoachmarkInternal = null;
    };
  }, []);

  // Follow the target every frame: covers sidebar animation, scroll, zoom, resize, accordion expand
  useEffect(() => {
    if (!isOpen) return undefined;
    let raf;
    const tick = () => {
      const el = ringRef.current;
      if (el && el.isConnected) {
        const next = readRect(el);
        setData((prev) => (prev && !isSameRect(prev.targetRect, next) ? { ...prev, targetRect: next } : prev));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  useLayoutEffect(() => {
    const pop = popoverRef.current;
    const el = ringRef.current;
    if (!pop || !el || !data || !el.isConnected) return;

    const w = pop.offsetWidth || COACHMARK_WIDTH;
    const h = pop.offsetHeight || COACHMARK_MIN_HEIGHT;
    const pos = calculatePlacement(el, w, h);

    pop.style.setProperty('--cm-top', `${pos.top}px`);
    pop.style.setProperty('--cm-left', `${pos.left}px`);

    if (pos.arrowLeft !== null) {
      pop.style.setProperty('--cm-arrow-left', `${pos.arrowLeft}px`);
    }
    if (pos.arrowTop !== null) {
      pop.style.setProperty('--cm-arrow-top', `${pos.arrowTop}px`);
    }

    setPlacement((prev) => (prev === pos.placement ? prev : pos.placement));
  }, [data]);

  if (!data) return null;

  return (
    <>
      {data.targetRect && (
        <svg className="coachmark-backdrop">
          <defs>
            <mask id="coachmark-stage-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={data.targetRect.left - (data.isHat ? 8 : (data.isNav ? 6 : 0))}
                y={data.targetRect.top - (data.isHat ? 8 : (data.isNav ? 6 : 0))}
                width={data.targetRect.width + (data.isHat ? 16 : (data.isNav ? 12 : 0))}
                height={data.targetRect.height + (data.isHat ? 16 : (data.isNav ? 12 : 0))}
                rx={data.isHat ? 8 : (data.isNav ? 6 : 4)}
                ry={data.isHat ? 8 : (data.isNav ? 6 : 4)}
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 27, 61, 0.55)"
            mask="url(#coachmark-stage-mask)"
          />
        </svg>
      )}

      <div
        ref={popoverRef}
        role="dialog"
        aria-live="polite"
        aria-label={data.title}
        className={`coachmark show cm-placement-${placement}`}
      >
        <div className="cm-arrow" />
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="cm-title">{data.title}</div>
          <button
            type="button"
            className="btn-close font-size-11"
            onClick={handleDismiss}
            aria-label={tHook('close')}
          />
        </div>
        <div className="cm-text">{data.text}</div>

        <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top border-200">
          {data.onBack ? (
            <button
              type="button"
              className="cm-btn-secondary"
              onClick={() => {
                if (data.onBack) data.onBack();
              }}
            >
              {data.backLabel || tHook('onboardingTour.back', 'Back')}
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="cm-btn"
            onClick={() => {
              if (data.onBtn) data.onBtn();
              handleDismiss();
            }}
          >
            {data.btnLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default Coachmark;
